import { useNavigate } from 'react-router-dom';
import { Wind, Square, Moon, HeartPulse, Zap } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { BREATHING_LIST, type BreathingTechnique } from '@/lib/breathing';

const ICON_MAP: Record<string, React.ComponentType<{ size?: number }>> = {
  'square': Square,
  'moon': Moon,
  'heart-rate-monitor': HeartPulse,
  'bolt': Zap,
};

/**
 * Карточки выбора дыхательной техники
 * Размещается внутри Релакс-таба
 */
export function BreathingTab() {
  const nav = useNavigate();
  const { haptic } = useTelegram();

  function openExercise(technique: BreathingTechnique) {
    haptic('medium');
    nav(`/breathing/${technique.key}`);
  }

  return (
    <div className="px-5 pt-2 pb-6">
      {/* Заголовок раздела */}
      <div className="flex items-center gap-2 mb-3">
        <Wind size={16} className="text-emerald-glow" />
        <span className="font-display font-semibold text-sm">Дыхательные практики</span>
      </div>
      <p className="text-xs text-ink-dim mb-4 leading-relaxed">
        Научно-доказанные техники для управления нервной системой
      </p>

      {/* Сетка карточек */}
      <div className="grid grid-cols-2 gap-3">
        {BREATHING_LIST.map((technique) => (
          <TechniqueCard
            key={technique.key}
            technique={technique}
            onClick={() => openExercise(technique)}
          />
        ))}
      </div>
    </div>
  );
}

interface TechniqueCardProps {
  technique: BreathingTechnique;
  onClick: () => void;
}

function TechniqueCard({ technique, onClick }: TechniqueCardProps) {
  const Icon = ICON_MAP[technique.icon] || Square;

  // Анимация круга — разная для каждой техники
  const pulseAnimation = getPulseAnimation(technique.key);

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative overflow-hidden rounded-2xl p-4 flex flex-col items-center min-h-[230px] active:scale-[0.98] transition-transform text-center"
      style={{
        background: `linear-gradient(160deg, ${technique.gradientFrom} 0%, ${technique.gradientTo} 100%)`,
      }}
    >
      {/* Иконка в углу */}
      <div
        className="absolute top-3 left-3 w-7 h-7 rounded-lg flex items-center justify-center"
        style={{
          background: `${technique.color}1f`,
          color: technique.color,
        }}
      >
        <Icon size={16} />
      </div>

      {/* Пульсирующий круг с ритмом внутри */}
      <div
        className="w-[100px] h-[100px] rounded-full flex items-center justify-center mb-3 mt-2"
        style={{
          background: `radial-gradient(circle at 35% 35%, ${technique.color}d9 0%, ${technique.color}80 45%, ${technique.color}1a 80%)`,
          boxShadow: `0 0 30px ${technique.colorRgba}`,
          animation: pulseAnimation,
        }}
      >
        <div
          className="text-lg font-display font-medium text-white tabular-nums tracking-wider"
          style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
        >
          {technique.rhythmShort}
        </div>
      </div>

      {/* Название */}
      <div className="text-sm font-display font-medium text-white mb-1 tracking-[0.3px]">
        {technique.name}
      </div>
      {/* Назначение */}
      <div className="text-[11px] text-white/60 leading-tight">
        {technique.purpose}
      </div>

      {/* Animations через style tag */}
      <style>{`
        @keyframes pulse-box {
          0%, 25% { transform: scale(0.85); }
          25.1%, 50% { transform: scale(1.15); }
          50.1%, 75% { transform: scale(1.15); }
          75.1%, 100% { transform: scale(0.85); }
        }
        @keyframes pulse-478 {
          0% { transform: scale(0.85); }
          21% { transform: scale(1.15); }
          57% { transform: scale(1.15); }
          100% { transform: scale(0.85); }
        }
        @keyframes pulse-coherent {
          0% { transform: scale(0.85); }
          50% { transform: scale(1.15); }
          100% { transform: scale(0.85); }
        }
        @keyframes pulse-sigh {
          0% { transform: scale(0.85); }
          25% { transform: scale(1.18); }
          37% { transform: scale(1.22); }
          100% { transform: scale(0.85); }
        }
      `}</style>
    </button>
  );
}

function getPulseAnimation(key: string): string {
  switch (key) {
    case 'box': return 'pulse-box 16s ease-in-out infinite';
    case '4_7_8': return 'pulse-478 19s ease-in-out infinite';
    case 'coherent': return 'pulse-coherent 10s ease-in-out infinite';
    case 'physiological_sigh': return 'pulse-sigh 8s ease-in-out infinite';
    default: return 'pulse-coherent 10s ease-in-out infinite';
  }
}
